import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { compileToSvg, compileToPdf } from "./typst";
import { TEMPLATES, DEFAULT_TEMPLATE, PHOTO_TEMPLATE_IDS, matchingTemplate, allowsPhoto } from "./templates";
import { parseCv } from "./cv-data";
import { toProfilePng, placeholderPng } from "./image";
import { encodeShareSource, decodeShareSource, readShareParam, buildShareUrl } from "./share";
import { Guide } from "./components/Guide";
import { AtsPanel } from "./components/AtsPanel";
import { CvForm } from "./components/CvForm";
import { ConfirmDialog } from "./components/ConfirmDialog";
import { StatusBar, type Status } from "./components/StatusBar";

const STORAGE_KEY = "cv-source-v1";
const DEBOUNCE_MS = 500;

export default function App() {
  const [source, setSource] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) ?? DEFAULT_TEMPLATE.source;
    } catch {
      return DEFAULT_TEMPLATE.source;
    }
  });
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("boot");
  const [guideOpen, setGuideOpen] = useState(false);
  const [atsOpen, setAtsOpen] = useState(false);
  // Left-pane mode: "form" (visual data form, default templates only) vs
  // "code" (raw Typst editor). Seeded from whether the initial source still
  // matches a template.
  const [mode, setMode] = useState<"form" | "code">(() =>
    parseCv(source) ? "form" : "code",
  );
  const [downloading, setDownloading] = useState(false);
  // Template pending a "replace current content?" confirmation.
  const [pendingTemplate, setPendingTemplate] = useState<string | null>(null);
  // Transient success note in the status bar (share link copied / loaded).
  const [notice, setNotice] = useState<string | null>(null);
  // Profile photo (PNG bytes) + a preview URL for the header thumbnail. Null
  // until the user uploads one. Kept in a ref so the debounced compile always
  // sees the latest without re-subscribing the effect.
  const [photo, setPhoto] = useState<Uint8Array | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const photoRef = useRef<Uint8Array | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // Serialize compilations: the wasm compiler is single-instance, and a
  // stale slow compile must never overwrite a newer result.
  const compileSeq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const compile = useCallback(async (src: string) => {
    const seq = ++compileSeq.current;
    setStatus("compiling");
    // Always keep an image mounted so photo templates compile even before an
    // upload: the user's photo if set, otherwise a neutral placeholder
    // (placeholderPng() caches internally, so this stays cheap).
    const img = photoRef.current ?? (await placeholderPng());
    const res = await compileToSvg(src, img);
    if (seq !== compileSeq.current) return; // superseded by a newer edit
    if (res.svg !== null) {
      setSvg(res.svg);
      setError(null);
      setStatus("ok");
    } else {
      // Keep the last good render on screen; surface the error in the bar.
      setError(res.error);
      setStatus("error");
    }
  }, []);

  // First compile on mount, then debounced on every edit + autosave.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, source);
      } catch {
        /* private mode — keep going without autosave */
      }
      void compile(source);
    }, status === "boot" ? 0 : DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, compile]);

  const flashNotice = useCallback((msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice((n) => (n === msg ? null : n)), 4000);
  }, []);

  // On first load, hydrate from a shared link (#src=…) if present. The shared
  // payload wins over the localStorage draft; the photo is never in the URL.
  useEffect(() => {
    const param = readShareParam(window.location.hash);
    if (!param) return;
    decodeShareSource(param)
      .then((src) => {
        setSource(src);
        history.replaceState(null, "", window.location.pathname + window.location.search);
        flashNotice("CV cargado desde enlace · vuelve a subir la foto si la plantilla la usa.");
      })
      .catch(() => {
        /* malformed link — ignore, keep the local/default source */
      });
  }, [flashNotice]);

  async function share() {
    try {
      const url = buildShareUrl(window.location.origin, await encodeShareSource(source));
      try {
        await navigator.clipboard.writeText(url);
        flashNotice("Enlace copiado al portapapeles (sin la foto).");
      } catch {
        window.prompt("Copia el enlace para compartir (sin la foto):", url);
      }
    } catch {
      setError("No se pudo generar el enlace para compartir.");
      setStatus("error");
    }
  }

  async function onPickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    try {
      const png = await toProfilePng(file);
      photoRef.current = png;
      setPhoto(png);
      setPhotoUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(new Blob([png as BlobPart], { type: "image/png" }));
      });
      void compile(source);
    } catch {
      setError("No se pudo procesar la imagen. Prueba con un JPG o PNG.");
      setStatus("error");
    }
  }

  function removePhoto() {
    photoRef.current = null;
    setPhoto(null);
    setPhotoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    void compile(source);
  }

  // The template the source currently corresponds to (null = custom design).
  // Still used only for the photo-hint id below — NOT for form availability.
  const activeTemplate = useMemo(() => matchingTemplate(source), [source]);
  // The form is available whenever the source carries an editable `#let cv`
  // data block, INDEPENDENT of whether the body still byte-matches a shipped
  // template. Gating on exact template equality stranded every draft saved
  // before a body/template tweak in code-only mode (the form silently vanished
  // until the user reloaded a default template) — the reported "a veces no
  // carga el formulario" bug. The form's applyCv only rewrites the cv block,
  // never the body, so a parseable block is sufficient and safe.
  const formAvailable = useMemo(() => parseCv(source) !== null, [source]);
  const showPhoto = useMemo(() => allowsPhoto(source), [source]);
  const isPhotoTemplate = !!activeTemplate && PHOTO_TEMPLATE_IDS.has(activeTemplate.id);
  // Effective view: the form only shows while a match still exists.
  const showForm = mode === "form" && formAvailable;

  function applyTemplate(t: (typeof TEMPLATES)[number]) {
    setSource(t.source);
    // Default templates open in the visual form.
    setMode(parseCv(t.source) ? "form" : "code");
  }

  function loadTemplate(id: string) {
    const t = TEMPLATES.find((t) => t.id === id);
    if (!t) return;
    // Ask before clobbering edited/custom content (an untouched template is
    // safe to swap silently).
    const dirty = source.trim() !== "" && !TEMPLATES.some((x) => x.source === source);
    if (dirty) {
      setPendingTemplate(id);
      return;
    }
    applyTemplate(t);
  }

  async function downloadPdf() {
    setDownloading(true);
    try {
      const img = photoRef.current ?? (await placeholderPng());
      const bytes = await compileToPdf(source, img);
      triggerDownload(new Blob([bytes as BlobPart], { type: "application/pdf" }), "cv.pdf");
    } catch {
      setError("No se pudo generar el PDF — revisa los errores de compilación.");
      setStatus("error");
    } finally {
      setDownloading(false);
    }
  }

  function downloadSource() {
    triggerDownload(new Blob([source], { type: "text/plain;charset=utf-8" }), "cv.typ");
  }

  const editorExtensions = useMemo(() => [EditorView.lineWrapping], []);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ── Header: brand + secondary utilities ─────────────────── */}
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-panel px-4 py-2">
        <a href="/" className="flex items-center" aria-label="CV generator — inicio">
          <img
            src="/brand/logo-curriculum-horizontal-light.svg"
            alt="The RED Signal Currículum"
            width="287"
            height="40"
            className="h-8 w-auto"
            decoding="async"
          />
        </a>
        <span className="font-mono text-sm text-mute">
          <span className="text-accent">▌</span> CV Generator
          <span className="ml-2 hidden text-[10px] uppercase tracking-widest sm:inline">
            premium · ats-friendly · 100% en tu navegador
          </span>
        </span>

        {/* Hidden photo input, triggered from the left-pane toolbar. */}
        <input ref={fileInput} type="file" accept="image/*" onChange={onPickPhoto} className="hidden" />

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 font-mono text-xs text-mute">
            plantilla
            <select
              onChange={(e) => {
                if (e.target.value) loadTemplate(e.target.value);
                e.target.value = "";
              }}
              defaultValue=""
              className="rounded border border-line bg-bg px-2 py-1 font-mono text-xs text-ink focus:border-accent focus:outline-none"
            >
              <option value="" disabled>
                elegir…
              </option>
              {TEMPLATES.map((t) => (
                <option key={t.id} value={t.id} title={t.description}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={share}
            title="Copiar un enlace con este CV (el texto viaja en la URL; la foto no)"
            className="rounded border border-line px-3 py-1 font-mono text-xs text-mute hover:border-accent hover:text-accent"
          >
            ⚿ compartir
          </button>
          <button
            onClick={downloadSource}
            title="Descargar el código fuente Typst (.typ)"
            className="rounded border border-line px-3 py-1 font-mono text-xs text-mute hover:border-accent hover:text-accent"
          >
            ↓ .typ
          </button>
          <a
            href="https://elcybercurioso.com"
            className="rounded border border-line px-3 py-1 font-mono text-xs text-mute hover:border-accent hover:text-accent"
          >
            ← blog
          </a>
        </div>
      </header>

      {/* ── Split: edit (form|code) / preview ───────────────────── */}
      <main className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-2">
        {/* Left pane — visual form or code editor */}
        <section
          aria-label="Edición del CV"
          className="flex min-h-[45vh] min-w-0 flex-col border-b border-line md:min-h-0 md:border-b-0 md:border-r"
        >
          <div className="flex items-center gap-2 border-b border-line bg-panel px-3 py-1.5">
            <div className="flex items-center gap-1 font-mono text-xs">
              {formAvailable && (
                <button
                  onClick={() => setMode("form")}
                  aria-pressed={showForm}
                  className={`rounded px-2.5 py-1 transition ${
                    showForm ? "bg-accent text-bg" : "text-mute hover:text-accent"
                  }`}
                >
                  formulario
                </button>
              )}
              <button
                onClick={() => setMode("code")}
                aria-pressed={!showForm}
                className={`rounded px-2.5 py-1 transition ${
                  !showForm ? "bg-accent text-bg" : "text-mute hover:text-accent"
                }`}
              >
                código
              </button>
            </div>

            {showPhoto && (
              <div className="ml-auto flex items-center gap-2">
                {photoUrl && (
                  <img
                    src={photoUrl}
                    alt="Foto de perfil"
                    className="h-10 w-10 rounded-full border border-line object-cover"
                  />
                )}
                <button
                  onClick={() => fileInput.current?.click()}
                  title="Añadir o cambiar la foto de perfil"
                  className="flex items-center gap-1 rounded border border-line px-2 py-1 font-mono text-xs text-mute hover:border-accent hover:text-accent"
                >
                  {photoUrl ? "cambiar foto" : "▣ subir foto"}
                </button>
                {photo && (
                  <button
                    onClick={removePhoto}
                    title="Quitar foto"
                    className="rounded border border-line px-1.5 py-1 font-mono text-xs text-mute hover:border-danger hover:text-danger"
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1">
            {showForm ? (
              <CvForm source={source} onChange={setSource} />
            ) : (
              <CodeMirror
                value={source}
                onChange={setSource}
                theme="dark"
                height="100%"
                style={{ height: "100%" }}
                extensions={editorExtensions}
                basicSetup={{ foldGutter: false, highlightActiveLine: true }}
              />
            )}
          </div>
        </section>

        {/* Right pane — live preview + primary actions */}
        <section aria-label="Vista previa del CV" className="flex min-h-0 flex-col bg-[#1b1b1f]">
          <div className="relative min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            {/* Round help button, inside the preview frame. */}
            <button
              onClick={() => setGuideOpen(true)}
              aria-label="Guía de uso"
              title="¿Cómo funciona? Guía de uso"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-line bg-panel/90 font-mono text-base text-mute shadow hover:border-accent hover:text-accent"
            >
              ?
            </button>

            {status === "boot" && (
              <p className="font-mono text-sm text-mute">
                cargando el motor (wasm)… la primera vista tarda unos segundos.
              </p>
            )}
            {svg && (
              <div
                className="typst-preview mx-auto w-full max-w-3xl space-y-6"
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            )}
          </div>

          {/* Primary actions, big and clear, under the preview. */}
          <div className="flex items-center gap-3 border-t border-line bg-panel p-3">
            <button
              onClick={downloadPdf}
              disabled={downloading || status === "boot"}
              className="flex-1 rounded-xl border-2 border-accent bg-accent px-5 py-3 font-mono text-sm font-bold uppercase tracking-wider text-bg transition hover:bg-accent/85 disabled:opacity-50"
            >
              {downloading ? "generando…" : "↓ descargar PDF"}
            </button>
            <button
              onClick={() => setAtsOpen(true)}
              title="Verificar el CV contra reglas ATS (100% en tu navegador)"
              className="rounded-xl border-2 border-line px-5 py-3 font-mono text-sm text-mute transition hover:border-accent hover:text-accent"
            >
              ✓ verificar ATS
            </button>
          </div>
        </section>
      </main>

      {/* ── Status bar ──────────────────────────────────────────── */}
      <StatusBar
        status={status}
        error={error}
        needsPhoto={isPhotoTemplate && !photo}
        notice={notice}
      />

      {pendingTemplate && (
        <ConfirmDialog
          title="cambiar de plantilla"
          message="Se reemplazará el contenido actual del editor por la plantilla elegida. Esta acción no se puede deshacer."
          confirmLabel="reemplazar"
          onConfirm={() => {
            const t = TEMPLATES.find((x) => x.id === pendingTemplate);
            if (t) applyTemplate(t);
            setPendingTemplate(null);
          }}
          onCancel={() => setPendingTemplate(null)}
        />
      )}
      {guideOpen && <Guide onClose={() => setGuideOpen(false)} />}
      {atsOpen && <AtsPanel source={source} onClose={() => setAtsOpen(false)} />}
    </div>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
